import { ReactElement } from 'react'
import { MenuItem, Select, Tooltip } from '@mui/material'
import useSelectTooltip from '../hooks/useSelectTooltip'

interface Props {
  autoApprove: boolean
  onChange: (autoApprove: boolean) => void
}

type ApprovalMode = 'manual' | 'auto'

/**
 * Whether this chat's changes are approved one by one or automatically: with
 * "Automatisch freigeben" the agent changes the project without asking.
 * Every change stays attributed (ai) in the project history; the setting
 * lasts for this session.
 */
const ApprovalModeSelect = ({ autoApprove, onChange }: Props): ReactElement => {
  const { tooltipProps, selectProps } = useSelectTooltip()

  return (
    <Tooltip
      title={
        autoApprove
          ? 'Der Assistent ändert das Projekt ohne Rückfrage. Alle Änderungen stehen in der Projekthistorie.'
          : 'Jede Änderung wird Ihnen vorher zur Freigabe angezeigt.'
      }
      {...tooltipProps}
      disableInteractive
      disableFocusListener
    >
      <Select<ApprovalMode>
        {...selectProps}
        size="small"
        variant="standard"
        disableUnderline
        value={autoApprove ? 'auto' : 'manual'}
        onChange={event => onChange(event.target.value === 'auto')}
        inputProps={{ 'aria-label': 'Freigabe von Änderungen' }}
        sx={{
          fontSize: 12,
          color: autoApprove ? 'warning.main' : 'text.secondary',
          '& .MuiSelect-select': { py: 0.25 },
        }}
        data-cy="ai-chat-auto-approve"
      >
        <MenuItem value="manual" dense>
          Manuell freigeben
        </MenuItem>
        <MenuItem value="auto" dense>
          Automatisch freigeben
        </MenuItem>
      </Select>
    </Tooltip>
  )
}

export default ApprovalModeSelect
